;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:native-teams-frontend":true});}catch(e){}}();import { cn } from '@workspace/ui/lib';
import * as React from 'react';

/**
 * Word vs character limits:
 * - Use `maxWords` to enforce and show a word count (displays "{n} words max" in the footer).
 * - For character limits, pass `description` with "{n} characters max" and set `maxLength` on the textarea.
 */
const Textarea = React.forwardRef<
  HTMLTextAreaElement,
  React.ComponentProps<'textarea'> & {
    /** Helper text below the textarea. For character limits, use "{n} characters max" with `maxLength`. */
    description?: string;
    /** Enforces a word limit and shows "{n} words max" in the footer. */
    maxWords?: number;
    readOnly?: boolean;
  }
>(function Textarea(
  {
    className,
    description,
    maxWords,
    value,
    onChange,
    placeholder,
    readOnly,
    disabled,
    defaultValue,
    ...props
  },
  ref
) {
  const text = typeof value === 'string' ? value : '';
  const resolvedPlaceholder = readOnly ? 'Not provided' : (placeholder ?? 'Type here');

  const handleChange = React.useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const incoming = e.target.value;
      const pieces = incoming.trim() ? incoming.trim().split(/\s+/) : [];
      const next =
        pieces.length > (maxWords ?? 100)
          ? `${pieces.slice(0, maxWords ?? 100).join(' ')} `
          : incoming;

      if (next !== incoming) {
        e.persist();
        Object.assign(e.target, { value: next });
      }
      onChange?.(e);
    },
    [maxWords, onChange]
  );

  const showFooter = !readOnly && (description || maxWords);

  return (
    <div className={cn(showFooter && 'space-y-2')}>
      <textarea
        ref={ref}
        data-slot="textarea"
        className={cn(
          'caret-primary dark:caret-neutral-100 placeholder:text-grey-700 dark:placeholder:text-grey-500 selection:bg-grey-100 aria-invalid:border-destructive dark:bg-input/30 [&:not(:placeholder-shown):not(:disabled)]:text-primary disabled:border-grey-100 dark:disabled:border-grey-900 disabled:text-grey-300 disabled:placeholder:text-grey-300 dark:disabled:text-grey-900 dark:disabled:placeholder:text-grey-900 flex min-h-32 w-full max-w-full resize-none rounded-2xl bg-transparent px-4 py-3 text-base leading-5 transition-[color,box-shadow] outline-none disabled:cursor-not-allowed dark:aria-invalid:border-red-400 dark:[&:not(:placeholder-shown):not(:disabled)]:text-neutral-100',
          readOnly
            ? 'border-grey-300 dark:border-grey-600 text-grey-600! mb-0 min-h-12 cursor-default border'
            : 'border-grey-300 hover:border-primary dark:border-grey-700 focus-visible:border-primary border shadow-xs',
          className
        )}
        placeholder={resolvedPlaceholder}
        readOnly={readOnly}
        disabled={disabled}
        {...props}
        {...(value !== undefined ? { value: text } : { defaultValue: defaultValue || '' })}
        onChange={handleChange}
      />
      <div className="flex items-center justify-between">
        {description && !readOnly && (
          <p
            className={cn('text-grey-500 text-sm', disabled && 'text-grey-300 dark:text-grey-900')}
          >
            {description}
          </p>
        )}
        {maxWords && !readOnly && (
          <span
            className={cn('text-grey-500 text-sm', disabled && 'text-grey-300 dark:text-grey-900')}
          >
            {maxWords} words max
          </span>
        )}
      </div>
    </div>
  );
});

Textarea.displayName = 'Textarea';

export { Textarea };
